import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'

export default function Solutions() {
  const { t } = useTranslation()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

  return (
    <section className="py-24 space-y-32" id="solutions" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Security Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row-reverse items-center gap-16"
        >
          <div className="lg:w-1/2 space-y-6">
            <h2 className="text-accent font-bold uppercase tracking-wider">
              {t('solutions.security.number')}. {t('solutions.security.category')}
            </h2>
            <h3 className="text-4xl font-bold">{t('solutions.security.title')}</h3>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t('solutions.security.description')}
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: 'security', key: 'turnstiles' },
                { icon: 'videocam', key: 'cameras' },
                { icon: 'monitor', key: 'monitors' },
                { icon: 'analytics', key: 'analytics' },
              ].map((feature, index) => (
                <motion.li
                  key={feature.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm"
                >
                  <span className="material-icons text-primary">{feature.icon}</span>
                  <span>{t(`solutions.security.features.${feature.key}`)}</span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="lg:w-1/2">
            <div className="bg-slate-200 dark:bg-slate-800 rounded-3xl p-8 relative overflow-hidden group">
              <img
                alt="Security surveillance systems"
                className="rounded-2xl shadow-lg transition-transform group-hover:scale-105 duration-700"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuCgL1e5ltN_VCkuZCXY0d-m0xaq9xEPDD1i4e8NT_WKWHEl1tA_zXZxIoTusqi-_ynKDq--aeGMjDnoikMUzrHXpAD_CYEAurlrkRtiaR7JG0sLkS__zEYzrxYAVFCRvZeaay_4xduONnxHZzsoWc_jNMNF0B2t-HKCF5Z2magsK3g2PjTw8XPN4ezkMvHloRnvFM5-PHXJNHMrC-yhEwDeB47mOmKvjLaAcmJAjpw9lMXl9tAKQ-vMghLTwgRhVcsr0jZavJTTJg"
              />
            </div>
          </div>
        </motion.div>

        {/* Experience Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-slate-50 dark:bg-slate-900/50 py-24"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="lg:w-1/2 space-y-6">
                <h2 className="text-primary font-bold uppercase tracking-wider">
                  {t('solutions.experience.number')}. {t('solutions.experience.category')}
                </h2>
                <h3 className="text-4xl font-bold">{t('solutions.experience.title')}</h3>
                <p className="text-lg text-slate-600 dark:text-slate-400">
                  {t('solutions.experience.description')}
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'queue', title: 'queue', desc: 'queueDesc' },
                    { icon: 'tv', title: 'display', desc: 'displayDesc' },
                    { icon: 'reviews', title: 'satisfaction', desc: 'satisfactionDesc' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      className="flex gap-4 p-4 rounded-2xl hover:bg-white dark:hover:bg-slate-800 transition-all cursor-default group"
                    >
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                        <span className="material-icons">{item.icon}</span>
                      </div>
                      <div>
                        <h4 className="font-bold">{t(`solutions.experience.${item.title}`)}</h4>
                        <p className="text-slate-500">{t(`solutions.experience.${item.desc}`)}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="lg:w-1/2 grid grid-cols-2 gap-4">
                <img
                  alt="Customer kiosk 1"
                  className="rounded-2xl shadow-lg mt-12"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuASfauwfS16li7fCKGwxXQpEuGC9uVqtFWr7w5cntDjD9srcTg2HhFpg84z2YSpgC_y6krAy0U0ScAKWt-xFvqvbFTJuyJ3OXsMgvwZ_s1qLOVIV8810r6Jkz3BdChtiq6jMTKp8YDYsLxK6G7MNiSlOWsll07nIHrg2Kq3k8JSeARBc144YFN1RQvtjAMyn40vVBJbBwPxSUfqUErV_9lKfQjFdqvC3Ff3k4M0YVfWQioK64oXhsqaqnVVJSRqB_2HdxUmJyU7MA"
                />
                <img
                  alt="Customer kiosk 2"
                  className="rounded-2xl shadow-lg"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuDdrhUX_Kr6BzWc56ODRtIvjcLw-dvwMR6at1F9mOJoKPtEnspqOou_t-CkMKlkv-Vrgq6X4LEhb-xKCJv0ivLHPNBljx_1zdD7365ta9JQ9wRtx-S6WGyW5qg1r0U5pNUnaHebAQ7jZ5vvUGYEDVKb-da6CtZ2RVudh1PHKkKSbja7B_cV9r0S-r1FzIJFLYvYu0lXEZolYsvYiOfXKjqEmE7SgQEmll1c4ePwRdPEIWj68YO3HrG6-Px8Rvp2s4SjNSgWQRgG_g"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Payment Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-accent font-bold uppercase tracking-wider">
                {t('solutions.payment.number')}. {t('solutions.payment.category')}
              </h2>
              <h3 className="text-4xl font-bold">{t('solutions.payment.title')}</h3>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                {t('solutions.payment.description')}
              </p>
              <div className="grid grid-cols-1 gap-6">
                <div className="p-6 bg-primary text-white rounded-2xl shadow-xl shadow-primary/20">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="material-icons text-3xl">point_of_sale</span>
                    <h4 className="text-xl font-bold">{t('solutions.payment.posTitle')}</h4>
                  </div>
                  <ul className="space-y-2 opacity-90">
                    {['fast', 'detection', 'nfc'].map((key) => (
                      <li key={key} className="flex items-center gap-2">
                        <span className="material-icons text-sm">check_circle</span>
                        {t(`solutions.payment.${key}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img
                alt="Payment terminal"
                className="rounded-3xl shadow-2xl"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDRoZv1EcwqZ8b3k1_Wttgu8SlYAcRFnpfuzxbU1M4m1WvMOWL8LGk5Q7f3kB_aCOTIiIGJHWAUTF8krvkodPELYR2A89BrZYxP01Md4N2v3JJP6J4DKFJ1AF2id0PUVsEm4MR0nxaBQLwkJmR1-sUVZypL8ymxYyjhN2t13VWsXLQ_jaAXyPJ-E0y6cK3FHjoSD9RbObI0xVeeO3CsmTR63SlyYz1Y9AQIGwgTfHEPKUpds3UROZbaIMCghGnbM770ANSBpHV17Q"
              />
            </div>
          </div>
        </motion.div>

        {/* Software Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-secondary text-white py-24"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-primary font-bold uppercase tracking-wider mb-4">
              {t('solutions.software.number')}. {t('solutions.software.category')}
            </h2>
            <h3 className="text-4xl font-bold mb-16">{t('solutions.software.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
              {[
                { key: 'appointments', icon: 'event_available' },
                { key: 'erp', icon: 'integration_instructions' },
                { key: 'communication', icon: 'chat' },
                { key: 'cloud', icon: 'cloud_done' },
              ].map((item, index) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10 hover:bg-white/20 transition-all"
                >
                  <span className="material-icons text-primary text-4xl mb-6">{item.icon}</span>
                  <h4 className="text-xl font-bold mb-3">{t(`solutions.software.${item.key}.title`)}</h4>
                  <p className="text-white/70 text-sm">
                    {t(`solutions.software.${item.key}.description`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
